import type { InputState, AudioConfig } from "@neon-arena/shared";
import { DEFAULT_GAME_CONFIG } from "@neon-arena/shared";
import type { VehicleSnapshot } from "../physics/VehicleController.js";
import type { AudioSystem } from "./AudioSystem.js";

// ── Module-level tunables (sourced from default config; used by exported pure functions) ──

const _d = DEFAULT_GAME_CONFIG.audio;
const BASE_RPM  = _d.baseRpm;
const IDLE_RPM  = _d.idleRpm;
const MAX_RPM   = _d.maxRpm;
const RPM_RANGE = MAX_RPM - IDLE_RPM;
const LOAD_BOOST = _d.loadBoost;
const RPM_SMOOTH = _d.rpmSmooth;

const GEAR_UP_THRESHOLDS = _d.gearUpThresholds;
const GEAR_DN_THRESHOLDS = _d.gearDnThresholds;

const FILTER_FREQ_IDLE       = _d.filterFreqIdle;
const FILTER_FREQ_MAX        = _d.filterMaxFreq;
const FILTER_THROTTLE_BOOST  = _d.filterThrottleBoost;
const FILTER_SMOOTH          = _d.filterSmooth;

// Loop trim — set loopStart/loopEnd to trim click artefacts once real OGGs are in place.
// A value of 0 for loopEnd means "use buffer.duration" (no trim).
const LOOP_LOW_START  = 0; const LOOP_LOW_END  = 0;
const LOOP_MID_START  = 0; const LOOP_MID_END  = 0;
const LOOP_HIGH_START = 0; const LOOP_HIGH_END = 0;
const LOOP_TYRE_START = 0; const LOOP_TYRE_END = 0;

// ── Exported pure functions (for unit tests — no Web Audio dependency) ──────

export function computeRpm(prev: number, speedRatio: number, throttle: number): number {
  const target = IDLE_RPM + speedRatio * RPM_RANGE + throttle * LOAD_BOOST;
  return prev + (target - prev) * RPM_SMOOTH;
}

export function computeGear(speedRatio: number, current: number): number {
  const upThresh = GEAR_UP_THRESHOLDS[current - 1];
  const dnThresh = GEAR_DN_THRESHOLDS[current - 2];
  if (upThresh !== undefined && speedRatio > upThresh) return Math.min(4, current + 1);
  if (dnThresh !== undefined && speedRatio < dnThresh) return Math.max(1, current - 1);
  return current;
}

export function computeSlip(
  steer: number,
  speedRatio: number,
  handbrake: boolean,
  brake: number,
  latRatio: number,
): number {
  return Math.min(
    1,
    Math.abs(steer) * speedRatio
    + (handbrake ? 1.5 : 0)
    + brake * 0.8
    + latRatio * 0.6,
  );
}

/**
 * Gear dip duration scaled by gearShiftSpeed.
 * Higher gearShiftSpeed → shorter dip. At 1.0 → unchanged.
 */
export function computeEffectiveGearDipMs(gearDipMs: number, gearShiftSpeed: number): number {
  return gearDipMs / gearShiftSpeed;
}

/**
 * RPM drop factor scaled by gearShiftSpeed.
 * Higher gearShiftSpeed → less severe drop (closer to 1.0). At 1.0 → unchanged.
 * Formula keeps the "distance from 1" proportional:  1 − (1 − base) / gearShiftSpeed
 */
export function computeEffectiveGearRpmDrop(gearRpmDrop: number, gearShiftSpeed: number): number {
  return 1 - ((1 - gearRpmDrop) / gearShiftSpeed);
}

export function computeFilterFreq(prev: number, rpmRatio: number, throttle: number): number {
  const target = FILTER_FREQ_IDLE
    + rpmRatio * (FILTER_FREQ_MAX - FILTER_FREQ_IDLE)
    + throttle * FILTER_THROTTLE_BOOST;
  return prev + (target - prev) * FILTER_SMOOTH;
}

// ── EngineAudio ─────────────────────────────────────────────────────────────

interface Track {
  src:    AudioBufferSourceNode;
  filter: BiquadFilterNode;
  gain:   GainNode;
}

export class EngineAudio {
  private ctx:          AudioContext | null = null;
  private engineMaster: GainNode     | null = null;

  private low:  Track | null = null;
  private mid:  Track | null = null;   // optional — null if engine_loop_3 missing
  private high: Track | null = null;
  private tyreSrc:  AudioBufferSourceNode | null = null;
  private tyreGain: GainNode              | null = null;
  private driftSrc:  AudioBufferSourceNode | null = null;
  private driftGain: GainNode              | null = null;

  private bufSqueal: [AudioBuffer | null, AudioBuffer | null] = [null, null];
  private bufShift:  AudioBuffer | null = null;
  private squealIdx  = 0;

  private cfg: AudioConfig = DEFAULT_GAME_CONFIG.audio;

  private rpm         = IDLE_RPM;
  private filterFreq  = FILTER_FREQ_IDLE;
  private currentGear = 1;
  private gearDipUntil = 0;  // performance.now() timestamp

  private slip     = 0;
  private prevSlip = 0;
  private lastSquealAt = 0;

  private ready    = false;
  private maxSpeed = 25;

  constructor(private audio: AudioSystem) {}

  // ── Init ──────────────────────────────────────────────────────────────────

  async init(maxSpeed: number, audioConfig?: AudioConfig): Promise<void> {
    this.maxSpeed = maxSpeed;
    if (audioConfig) this.cfg = audioConfig;

    const { ctx, master } = this.audio.ensureContext();
    this.ctx = ctx;

    this.engineMaster = ctx.createGain();
    this.engineMaster.gain.value = this.cfg.engineMasterGain;
    this.engineMaster.connect(master);

    // Asset map — same files used for all vehicles; physics parameters drive variation.
    // idle.ogg     → low RPM layer
    // engine_loop_1.ogg → mid RPM layer
    // engine_loop_2.ogg → high RPM layer
    // tire_screech_quiet_louder_louder.ogg → continuous skid loop
    // handbrake_turn_screech.ogg → one-shot screech A
    // car_start_skid_crash.ogg   → one-shot screech B (fallback)
    const urls = [
      "/audio/cars/idle.ogg",                             // [0] low
      "/audio/cars/engine_loop_1.ogg",                   // [1] mid
      "/audio/cars/engine_loop_2.ogg",                   // [2] high
      "/audio/cars/tire_screech_quiet_louder_louder.ogg", // [3] tyre loop
      "/audio/cars/handbrake_turn_screech.ogg",           // [4] squeal A
      "/audio/cars/car_start_skid_crash.ogg",             // [5] squeal B
    ];

    const results = await Promise.allSettled(urls.map((u) => fetchDecode(ctx, u)));
    const buf = results.map((r, i) => {
      if (r.status === "fulfilled") {
        console.log("[EngineAudio] loaded:", urls[i]);
        return r.value;
      }
      console.warn("[EngineAudio] skipped:", urls[i]);
      return null;
    });

    const [bufLow, bufMid, bufHigh, bufTyre, sq1, sq2] = buf;

    if (sq1) console.log(`[EngineAudio] squeal A duration: ${sq1.duration.toFixed(2)}s`);
    if (sq2) console.log(`[EngineAudio] squeal B duration: ${sq2.duration.toFixed(2)}s`);

    this.low  = bufLow  ? makeLoop(ctx, bufLow,  this.engineMaster!, LOOP_LOW_START,  LOOP_LOW_END)  : null;
    this.mid  = bufMid  ? makeLoop(ctx, bufMid,  this.engineMaster!, LOOP_MID_START,  LOOP_MID_END)  : null;
    this.high = bufHigh ? makeLoop(ctx, bufHigh, this.engineMaster!, LOOP_HIGH_START, LOOP_HIGH_END) : null;

    if (bufTyre) {
      const tyreGain = ctx.createGain();
      tyreGain.gain.value = 0;
      tyreGain.connect(this.engineMaster!);
      const tyreSrc = ctx.createBufferSource();
      tyreSrc.buffer = bufTyre;
      tyreSrc.loop = true;
      if (LOOP_TYRE_START > 0) tyreSrc.loopStart = LOOP_TYRE_START;
      if (LOOP_TYRE_END   > 0) tyreSrc.loopEnd   = LOOP_TYRE_END;
      tyreSrc.connect(tyreGain);
      tyreSrc.start(0);
      this.tyreSrc  = tyreSrc;
      this.tyreGain = tyreGain;
    }

    // Drift screech — lower-pitched loop using the handbrake screech sample
    if (sq1) {
      const driftGain = ctx.createGain();
      driftGain.gain.value = 0;
      driftGain.connect(this.engineMaster!);
      const driftSrc = ctx.createBufferSource();
      driftSrc.buffer = sq1;
      driftSrc.loop = true;
      driftSrc.playbackRate.value = 0.65;
      driftSrc.connect(driftGain);
      driftSrc.start(0);
      this.driftSrc  = driftSrc;
      this.driftGain = driftGain;
    }

    this.bufSqueal = [sq1, sq2];
    this.bufShift  = null;  // no gear_shift asset available

    // Initial gains
    if (this.low)  this.low.gain.gain.value  = 1;
    if (this.high) this.high.gain.gain.value = 0;
    if (this.mid)  this.mid.gain.gain.value  = 0;

    this.ready = true;
  }

  // ── Per-frame update ──────────────────────────────────────────────────────

  update(input: InputState, snap: VehicleSnapshot, _dt: number, gearShiftSpeed = 1.0): void {
    if (!this.ready || !this.engineMaster) return;

    const now = performance.now();
    const speed = Math.abs(snap.speed);
    const speedRatio = Math.min(1, speed / this.maxSpeed);

    // ── RPM ────────────────────────────────────────────────────────────────
    this.rpm = computeRpm(this.rpm, speedRatio, input.throttle);
    const playbackRate = Math.max(this.cfg.playbackMin, Math.min(this.cfg.playbackMax, this.rpm / BASE_RPM));

    if (this.low)  this.low.src.playbackRate.value  = playbackRate;
    if (this.high) this.high.src.playbackRate.value = playbackRate;
    if (this.mid)  this.mid.src.playbackRate.value  = playbackRate;

    // ── Filter (tonal variation) ───────────────────────────────────────────
    const rpmRatio = Math.min(1, Math.max(0, (this.rpm - IDLE_RPM) / RPM_RANGE));
    this.filterFreq = computeFilterFreq(this.filterFreq, rpmRatio, input.throttle);

    if (this.low)  this.low.filter.frequency.value  = this.filterFreq;
    if (this.high) this.high.filter.frequency.value = this.filterFreq;
    if (this.mid)  this.mid.filter.frequency.value  = this.filterFreq;

    // ── Crossfade gain + coast scale ───────────────────────────────────────
    const coastScale = 1.0 - (1.0 - this.cfg.coastGainScale) * (1.0 - input.throttle);

    if (this.mid) {
      // 3-way blend: low dominant at low RPM, mid peaks at mid, high at high
      if (this.low)  this.low.gain.gain.value  = Math.max(0, 1 - rpmRatio * 2)              * coastScale;
      if (this.mid)  this.mid.gain.gain.value  = Math.max(0, 1 - Math.abs(rpmRatio - 0.5) * 2) * coastScale;
      if (this.high) this.high.gain.gain.value = Math.max(0, rpmRatio * 2 - 1)              * coastScale;
    } else {
      // 2-way crossfade
      if (this.low)  this.low.gain.gain.value  = Math.max(0, 1 - rpmRatio * 1.5) * coastScale;
      if (this.high) this.high.gain.gain.value = Math.min(1, rpmRatio * 1.5)      * coastScale;
    }

    // ── Gear system ────────────────────────────────────────────────────────
    const nextGear = computeGear(speedRatio, this.currentGear);
    if (nextGear !== this.currentGear) {
      this.currentGear  = nextGear;
      this.rpm         *= computeEffectiveGearRpmDrop(this.cfg.gearRpmDrop, gearShiftSpeed);
      this.gearDipUntil = now + computeEffectiveGearDipMs(this.cfg.gearDipMs, gearShiftSpeed);
      if (this.bufShift) this.playOneShot(this.bufShift);
    }

    this.engineMaster.gain.value = now < this.gearDipUntil ? this.cfg.gearDipGain : this.cfg.engineMasterGain;

    // ── Tyre slip ──────────────────────────────────────────────────────────
    const sinR = Math.sin(snap.rotY);
    const cosR = Math.cos(snap.rotY);
    const lateralVelocity = snap.vx * cosR - snap.vz * sinR;
    const latRatio = Math.min(1, Math.abs(lateralVelocity) / Math.max(1, this.maxSpeed * 0.35));

    // Brake screech: responds directly to brake pedal at speed
    const brakeSlip = Math.min(1, input.brake * speedRatio * 2);
    // Drift screech: handbrake + actual lateral velocity at speed
    const driftSlip = input.handbrake ? Math.min(1, speedRatio * 1.5 + latRatio) : 0;

    if (this.tyreGain)  this.tyreGain.gain.value  = brakeSlip;
    if (this.tyreSrc)   this.tyreSrc.playbackRate.value = this.cfg.tyreRateBase + brakeSlip * this.cfg.tyreRateRange;
    if (this.driftGain) this.driftGain.gain.value = driftSlip;

    // Combined slip for one-shot squeal detection (steer + handbrake + brake + lateral)
    this.slip = computeSlip(input.steer, speedRatio, input.handbrake, input.brake, latRatio);

    const slipDelta = this.slip - this.prevSlip;
    if (slipDelta > this.cfg.squealDeltaThresh && now - this.lastSquealAt > this.cfg.squealCooldownMs) {
      const buf = this.bufSqueal[this.squealIdx % 2];
      this.squealIdx++;
      if (buf) this.playOneShot(buf);
      this.lastSquealAt = now;
    }
    this.prevSlip = this.slip;
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  dispose(): void {
    for (const track of [this.low, this.mid, this.high]) {
      if (!track) continue;
      try { track.src.stop(); } catch { /* already stopped or never started */ }
    }
    try { this.tyreSrc?.stop(); } catch { /* already stopped */ }
    try { this.driftSrc?.stop(); } catch { /* already stopped */ }
    try { this.engineMaster?.disconnect(); } catch { /* never connected */ }

    this.low = this.mid = this.high = null;
    this.tyreSrc = this.tyreGain = null;
    this.driftSrc = this.driftGain = null;
    this.engineMaster = null;
    this.ctx = null;
    this.ready = false;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private playOneShot(buffer: AudioBuffer): void {
    if (!this.ctx || !this.engineMaster) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.engineMaster);
    const capSec = this.cfg.squealCooldownMs / 1000;
    src.start();
    src.stop(this.ctx.currentTime + Math.min(buffer.duration, capSec));
  }
}

// ── Module-level helpers ───────────────────────────────────────────────────

async function fetchDecode(ctx: AudioContext, url: string): Promise<AudioBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const ab = await res.arrayBuffer();
  return ctx.decodeAudioData(ab);
}

function makeLoop(
  ctx: AudioContext,
  buffer: AudioBuffer,
  master: GainNode,
  loopStart: number,
  loopEnd: number,
): Track {
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = FILTER_FREQ_IDLE;
  filter.Q.value = 0.7;

  const gain = ctx.createGain();
  gain.gain.value = 0;

  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop   = true;
  if (loopStart > 0) src.loopStart = loopStart;
  if (loopEnd   > 0) src.loopEnd   = loopEnd;

  src.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  src.start(0);

  return { src, filter, gain };
}
